import { useCallback, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { SegmentedBannerGrid } from '~/src/components/SegmentedBanner/SegmentedBannerGrid';
import { usePrepareQueryData } from '~/src/helpers/hooks/usePrepareQueryData';
import { scrollToAnchor } from '~/src/helpers/scrollToAnchor';
import { TState } from '~/src/store';
import { fetchTvPackages } from '~/src/store/actions/tvPackages';

import DetailsLayout from './components/DetailsLayout';
import { TryAndBuyConnectionForTvServices } from './components/TryAndBuyConnectionForTvServices';
import PageHeader from './sections/head';
import Packs from './sections/packs';
import Services from './sections/services';
import Subscriptions from './sections/subscriptions';
import { fetchSubscriptions } from './store/actions/fetchSubscriptions';
import { defaultDetailsState, ESectionId, TDetailsType } from './types';

import { PageContainer } from './styled';

const PackagesLayout = (): JSX.Element => {
    const dispatch = useDispatch();
    const { subscriptionId } = usePrepareQueryData();

    const { auth: { isAuth }, subscriptions } = useSelector((state: TState) => ({
        auth: state.auth,
        subscriptions: state.vas.subscriptions,
    }), shallowEqual);

    const [detailsState, setDetailsState] = useState(defaultDetailsState);

    const onShowDetails = useCallback((type: TDetailsType) => {
        setDetailsState({
            isOpen: true,
            type,
        });
    }, []);

    const onHideDetails = useCallback(() => {
        setDetailsState(defaultDetailsState);
    }, []);

    useEffect(() => {
        dispatch(fetchTvPackages());
        dispatch(fetchSubscriptions());
    }, [isAuth, dispatch]);

    useEffect(() => {
        if (subscriptionId && ((isAuth && !!subscriptions.length) || !isAuth)) {
            onShowDetails('subscriptions');
        }
    }, [subscriptionId, subscriptions, isAuth, onShowDetails]);

    useEffect(() => {
        if (location.hash) {
            const sectionId = location.hash.replace(/#/i, '');
            for (const key in ESectionId) {
                if (key === sectionId) {
                    scrollToAnchor(sectionId);
                }
            }
        }
    }, []);

    return (
        <>
            {detailsState.isOpen
                ? <DetailsLayout
                    type={detailsState.type}
                    onGoBack={onHideDetails}
                    data-test='details-layout'
                />
                : <PageContainer data-test='content-layout'>
                    <PageHeader/>
                    <SegmentedBannerGrid alias={'lk_channel'} margin='4px 0'/>
                    <Packs sectionId={ESectionId.packs} onShowMore={onShowDetails}/>
                    <Subscriptions sectionId={ESectionId.subscriptions} onShowMore={onShowDetails}/>
                    <Services sectionId={ESectionId.services}/>
                </PageContainer>
            }
            <TryAndBuyConnectionForTvServices/>
        </>
    );
};

export default PackagesLayout;
